import { useState, useEffect } from 'react'
import { List, Cell, Tag, Button, Empty, Loading, Toast, Uploader } from 'react-vant'
import { ArrowLeft, Edit, Success } from '@react-vant/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import request from '../../api/request'

export default function RefereeWorkbench() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const code = searchParams.get('code')
    const scheduleId = searchParams.get('scheduleId')

    const [loading, setLoading] = useState(true)
    const [info, setInfo] = useState(null)
    const [athletes, setAthletes] = useState([])
    const [scores, setScores] = useState({}) // { registration_id: score }
    const [submitting, setSubmitting] = useState(false)
    const [scheduleInfo, setScheduleInfo] = useState(null)
    const [fileList, setFileList] = useState([]) // { url: string, status?: string, message?: string }

    useEffect(() => {
        fetchData()
    }, [scheduleId, code])

    const fetchData = async () => {
        setLoading(true)
        try {
            const res = await request.post(`/v2/sports/schedules/${scheduleId}/verify-code/`, { code })
            if (res.success) {
                setInfo(res.schedule_info)
                setScheduleInfo(res.schedule_info)
                setAthletes(res.athletes)
                // 初始化分数（回显已有成绩）
                const initialScores = {}
                res.athletes.forEach(a => {
                    initialScores[a.registration_id] = a.performance || ''
                })
                setScores(initialScores)

                // 初始化已上传的照片
                if (res.schedule_info.live_photos) {
                    const envBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
                    let baseUrl = envBaseUrl
                    if (envBaseUrl.startsWith('http')) {
                        try {
                            baseUrl = new URL(envBaseUrl).origin
                        } catch (e) {
                            baseUrl = envBaseUrl.replace('/api', '')
                        }
                    } else {
                        baseUrl = envBaseUrl.replace('/api', '')
                    }
                    setFileList(res.schedule_info.live_photos.map(url => ({
                        url: url.startsWith('http') ? url : `${baseUrl}${url}`
                    })))
                }
            }
        } catch (e) {
            console.error('获取赛程数据失败:', e)
            const status = e?.response?.status
            const detail = e?.detail || e?.response?.data?.detail
            if (status === 409 && typeof detail === 'string') {
                Toast.fail({ message: detail, duration: 4000 })
                navigate('/sports/referee/login')
                return
            }
            if (status === 401) {
                Toast.fail({ message: detail || '口令错误，请重新输入', duration: 3000 })
                navigate('/sports/referee/login')
                return
            }
            if (status === 429) {
                Toast.fail({ message: detail || '口令错误次数过多，请稍后再试', duration: 4000 })
                navigate('/sports/referee/login')
                return
            }
            Toast.fail('验证失效，请重新登录')
            navigate('/sports/referee/login')
        } finally {
            setLoading(false)
        }
    }

    const handleScoreChange = (regId, val) => {
        setScores(prev => ({ ...prev, [regId]: val }))
    }

    // 解决 afterRead 不触发的万能 fallback
    useEffect(() => {
        const unuploaded = fileList.filter(f => f.file && !f.status && !f.url?.startsWith('/uploads'))
        if (unuploaded.length > 0) {
            handleAfterRead(unuploaded)
        }
    }, [fileList])

    const handleAfterRead = async (fileData) => {
        const items = Array.isArray(fileData) ? fileData : [fileData]
        
        for (const item of items) {
            if (!item.file) continue
            
            item.status = 'uploading'
            item.message = '上传中...'
            setFileList([...fileList])

            try {
                const formData = new FormData()
                formData.append('file', item.file)
                formData.append('sports_meet_id', scheduleInfo.sports_meet_id)
                formData.append('schedule_id', scheduleId)

                const uploadRes = await request.post('/v2/sports/live/upload-photo/', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    params: { referee_code: code }
                })

                if (uploadRes.success) {
                    item.status = 'done'
                    item.message = '上传成功'
                    item.url = uploadRes.photo_url
                    Toast.success('照片实时同步成功')
                } else {
                    item.status = 'failed'
                    item.message = '上传失败'
                }
            } catch (err) {
                console.error('Photo upload failed:', err)
                item.status = 'failed'
                item.message = '上传失败'
            }
            setFileList([...fileList])
        }
    }

    const handleDeletePhoto = async (item) => {
        try {
            const res = await request.delete('/v2/sports/live/photo/', {
                params: {
                    sports_meet_id: scheduleInfo.sports_meet_id,
                    schedule_id: scheduleId,
                    photo_url: item.url,
                    referee_code: code
                }
            })
            if (res.success) {
                Toast.success('照片已从云端移除')
                return true
            }
        } catch (e) {
            console.error('Delete photo failed:', e)
            Toast.fail('删除失败')
            return false
        }
        return true
    }

    const handleSubmit = async () => {
        const resultCount = Object.values(scores).filter(v => v !== '').length
        if (resultCount === 0) {
            Toast.info('请至少录入一个成绩')
            return
        }

        setSubmitting(true)
        try {
            const results = Object.entries(scores)
                .filter(([_, val]) => val !== '')
                .map(([regId, val]) => {
                    const athlete = athletes.find(a => a.registration_id === parseInt(regId))
                    return {
                        registration_id: parseInt(regId),
                        performance: val,
                        performance_value: parseFloat(val),
                        student_id: athlete?.id,
                        student_name: athlete?.name || '未知'
                    }
                })

            const payload = {
                sports_meet_id: scheduleInfo.sports_meet_id,
                event_id: scheduleInfo.event_id,
                schedule_id: parseInt(scheduleId),
                results: results
            }

            await request.post('/v2/sports/results/referee-batch/', payload, {
                params: { referee_code: code }
            })
            Toast.success('提交成功')
            navigate(`/sports/referee/success?code=${code}`, { replace: true })
        } catch (e) {
            console.error('提交成绩失败:', e)
            Toast.fail('提交失败，请检查网络')
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--canvas-bg, #f8fafc)' }}>
                <Loading type="spinner" color="#056b4f" vertical>加载执裁任务...</Loading>
            </div>
        )
    }

    if (!scheduleInfo) {
        return (
            <div style={{ minHeight: '100vh', background: 'var(--canvas-bg, #f8fafc)', padding: '60px 16px' }}>
                <Empty description="未获取到赛程信息" />
            </div>
        )
    }

    const isCompleted = scheduleInfo?.status === 'completed'

    return (
        <div className="referee-workbench" style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh', paddingBottom: 88 }}>
            {/* 沉浸式星瀚空天蓝页眉 */}
            <div style={{
                background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
                padding: '44px 16px 20px',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* 装饰半透圆 */}
                <div style={{
                    position: 'absolute', top: -30, right: -30,
                    width: 140, height: 140, borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.08)'
                }} />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <div
                        onClick={() => navigate(-1)}
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <ArrowLeft color="#fff" fontSize={18} />
                    </div>

                    <div style={{ textAlign: 'center' }}>
                        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.2px' }}>
                            {scheduleInfo?.event_name || '执裁录入'}
                        </h1>
                        <p style={{ margin: '2px 0 0', fontSize: 11.5, color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>
                            {info?.round_name} · {info?.group_name} · {isCompleted ? '已完成' : '成绩录入中'}
                        </p>
                    </div>

                    <div style={{ width: 36 }} />
                </div>
            </div>

            {/* 项目概要卡片 */}
            <div style={{
                background: '#ffffff',
                margin: '14px 16px',
                padding: 16,
                borderRadius: 16,
                boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                border: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                            {info?.group_name}
                        </h3>
                        <Tag
                            round
                            style={{
                                background: isCompleted ? '#e8f9f0' : '#f0fdf4',
                                color: isCompleted ? '#00b578' : '#056b4f',
                                border: 'none',
                                fontWeight: 600,
                                fontSize: 11,
                                padding: '1px 8px'
                            }}
                        >
                            {isCompleted ? '已完赛' : '录入中'}
                        </Tag>
                    </div>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
                        {info?.round_name} · 📍 {info?.venue || '主跑道'}
                    </p>
                </div>
                <span style={{
                    background: '#f0fdf4',
                    color: '#056b4f',
                    fontWeight: 700,
                    fontSize: 12,
                    padding: '4px 10px',
                    borderRadius: 999
                }}>
                    {athletes?.length || 0} 人
                </span>
            </div>

            {/* 运动员录入列表 */}
            <div style={{ marginTop: 12 }}>
                <List>
                    {athletes?.map((athlete, index) => (
                        <div
                            key={athlete.registration_id}
                            style={{
                                background: '#ffffff',
                                margin: '0 16px 10px',
                                padding: '14px 16px',
                                borderRadius: 16,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                                border: '1px solid #f1f5f9'
                            }}
                        >
                            <div style={{
                                width: 36, height: 36,
                                background: '#f0fdf4',
                                borderRadius: 18,
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                fontSize: 14, fontWeight: 800, color: '#056b4f',
                                border: '1px solid #a7f3d0'
                            }}>
                                {athlete.bib || (index + 1)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{athlete.name}</div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{athlete.class}</div>
                            </div>
                            <div style={{ width: 110 }}>
                                <input
                                    type="number"
                                    placeholder="成绩"
                                    value={scores[athlete.registration_id] || ''}
                                    onChange={(e) => handleScoreChange(athlete.registration_id, e.target.value)}
                                    style={{
                                        width: '100%',
                                        border: '1px solid #e2e8f0',
                                        background: '#f8fafc',
                                        padding: '8px 10px',
                                        borderRadius: 10,
                                        textAlign: 'right',
                                        fontSize: 16,
                                        fontWeight: 700,
                                        color: '#056b4f',
                                        outline: 'none'
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </List>
            </div>

            {/* 实况照片上传卡片 */}
            <div style={{
                background: '#ffffff',
                margin: '14px 16px',
                padding: 16,
                borderRadius: 16,
                boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                border: '1px solid #f1f5f9'
            }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                    📸 实况风采 (现场照片上传)
                </h3>
                <Uploader
                    value={fileList}
                    onChange={setFileList}
                    maxCount={6}
                    afterRead={handleAfterRead}
                    onDelete={handleDeletePhoto}
                    uploadText="点击上传"
                />
                <p style={{ margin: '10px 0 0', fontSize: 12, color: '#056b4f', fontWeight: 500 }}>
                    💡 提示：照片选中后即自动同步至赛事直播大厅。
                </p>
            </div>

            {/* 底部提交栏 */}
            <div style={{
                position: 'fixed',
                bottom: 0, left: 0, right: 0,
                padding: '12px 16px',
                background: '#ffffff',
                boxShadow: '0 -4px 16px rgba(15, 23, 42, 0.06)',
                zIndex: 40,
                borderTop: '1px solid #e2e8f0'
            }}>
                <Button
                    block
                    round
                    loading={submitting}
                    onClick={handleSubmit}
                    icon={<Success />}
                    style={{
                        background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
                        border: 'none',
                        color: '#ffffff',
                        fontSize: 15,
                        fontWeight: 700,
                        height: 44,
                        boxShadow: '0 4px 14px rgba(5, 107, 79, 0.35)'
                    }}
                >
                    提交本组报名成绩
                </Button>
            </div>
        </div>
    )
}
